import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Autocomplete,
  Chip,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material'
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material'
import { ServicePackageFormData } from '../../types/servicePackage'
import {
  specialties,
  bodyRegions,
  states,
} from '../../data/servicePackageMockData'
import { getFieldError } from '../../utils/servicePackageValidation'

interface Section6VisibilityMatchingProps {
  formData: ServicePackageFormData
  onUpdate: (data: Partial<ServicePackageFormData>) => void
  errors: any
  onSaveAsDraft: () => void
  onPublish: () => void
  isSubmitting: boolean
  mode?: 'create' | 'edit'
  saveLabel?: string
  publishLabel?: string
  originalStatus?: ServicePackageFormData['status']
}

export const Section6VisibilityMatching: React.FC<
  Section6VisibilityMatchingProps
> = ({
  formData,
  onUpdate,
  errors,
  onSaveAsDraft,
  onPublish,
  isSubmitting,
  mode = 'create',
  saveLabel,
  publishLabel,
  originalStatus = 'draft',
}) => {
  const [newKeyword, setNewKeyword] = useState('')
  const isEditMode = mode === 'edit'
  const draftButtonLabel =
    saveLabel ||
    (isEditMode
      ? originalStatus === 'published'
        ? 'Save Changes as Draft'
        : 'Save Changes & Keep Draft'
      : 'Save as Draft')
  const publishButtonLabel =
    publishLabel ||
    (isEditMode
      ? originalStatus === 'published'
        ? 'Save Changes & Publish'
        : 'Save Changes & Publish Package'
      : 'Publish Service Package')
  const isPublishedEdit = isEditMode && originalStatus === 'published'
  const isDraftEdit = isEditMode && originalStatus !== 'published'

  const handleSpecialtyChange = (selectedSpecialties: any[]) => {
    onUpdate({
      visibilityMatching: {
        ...formData.visibilityMatching,
        specialties: selectedSpecialties.map(s => s.id),
      },
    })
  }

  const handleBodyRegionChange = (selectedRegions: any[]) => {
    onUpdate({
      visibilityMatching: {
        ...formData.visibilityMatching,
        bodyRegions: selectedRegions.map(r => r.id),
      },
    })
  }

  const handleStateChange = (selectedStates: any[]) => {
    onUpdate({
      visibilityMatching: {
        ...formData.visibilityMatching,
        states: selectedStates.map(s => s.id),
      },
    })
  }

  const addKeyword = () => {
    if (
      newKeyword.trim() &&
      !(formData.visibilityMatching.keywords || []).includes(newKeyword.trim())
    ) {
      onUpdate({
        visibilityMatching: {
          ...formData.visibilityMatching,
          keywords: [
            ...(formData.visibilityMatching.keywords || []),
            newKeyword.trim(),
          ],
        },
      })
      setNewKeyword('')
    }
  }

  const removeKeyword = (keywordToRemove: string) => {
    onUpdate({
      visibilityMatching: {
        ...formData.visibilityMatching,
        keywords: (formData.visibilityMatching.keywords || []).filter(
          keyword => keyword !== keywordToRemove
        ),
      },
    })
  }

  const handleKeywordKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      addKeyword()
    }
  }

  const specialtiesError = getFieldError(errors, 'specialties')
  const bodyRegionsError = getFieldError(errors, 'bodyRegions')
  const keywordsError = getFieldError(errors, 'keywords')
  const statesError = getFieldError(errors, 'states')

  const selectedSpecialties = specialties.filter(
    s => formData.visibilityMatching.specialties?.includes(s.id) || false
  )

  const selectedBodyRegions = bodyRegions.filter(
    r => formData.visibilityMatching.bodyRegions?.includes(r.id) || false
  )

  const selectedStates = states.filter(
    s => formData.visibilityMatching.states?.includes(s.id) || false
  )

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Visibility & Matching
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure how your service package appears in search results and which
        patients can find it.
      </Typography>

      {/* Specialties */}
      {/* <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Specialties
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the therapeutic specialties that apply to your service
            package. Patients will find your package when searching for these
            specialties.
          </Typography>

          {specialtiesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {specialtiesError}
            </Alert>
          )}

          <Autocomplete
            multiple
            options={specialties}
            getOptionLabel={option => `${option.name} - ${option.category}`}
            value={selectedSpecialties}
            onChange={(_, newValue) => handleSpecialtyChange(newValue)}
            renderInput={params => (
              <TextField
                {...params}
                label="Select Specialties"
                placeholder="Search and select specialties..."
                error={!!specialtiesError}
                helperText={
                  specialtiesError ||
                  'Choose all specialties that apply to your service'
                }
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={option.name}
                  color="primary"
                  variant="outlined"
                />
              ))
            }
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.category} • {option.description}
                  </Typography>
                </Box>
              </Box>
            )}
          />

          {/* Selected Specialties Summary */}
      {/* {selectedSpecialties.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Specialties ({selectedSpecialties.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedSpecialties.map(specialty => (
                  <Chip
                    key={specialty.id}
                    label={specialty.name}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card> */}

      {/* Body Regions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Body Regions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the body regions or areas that your service package
            addresses. This helps patients find relevant treatment options.
          </Typography>

          {bodyRegionsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bodyRegionsError}
            </Alert>
          )}

          <Autocomplete
            multiple
            options={bodyRegions}
            getOptionLabel={option => `${option.name} - ${option.category}`}
            value={selectedBodyRegions}
            onChange={(_, newValue) => handleBodyRegionChange(newValue)}
            renderInput={params => (
              <TextField
                {...params}
                label="Select Body Regions"
                placeholder="Search and select body regions..."
                error={!!bodyRegionsError}
                helperText={
                  bodyRegionsError ||
                  'Choose all body regions your service addresses'
                }
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={option.name}
                  color="secondary"
                  variant="outlined"
                />
              ))
            }
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.category} • {option.description}
                  </Typography>
                </Box>
              </Box>
            )}
          />

          {/* Selected Body Regions Summary */}
          {selectedBodyRegions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Body Regions ({selectedBodyRegions.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedBodyRegions.map(region => (
                  <Chip
                    key={region.id}
                    label={region.name}
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Keywords */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Keywords & Tags
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add relevant keywords and tags to improve discoverability. Think
            about terms patients might search for when looking for your
            services.
          </Typography>

          {keywordsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {keywordsError}
            </Alert>
          )}

          {/* Add Keyword Input */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Add Keyword"
                value={newKeyword}
                onChange={e => setNewKeyword(e.target.value)}
                onKeyPress={handleKeywordKeyPress}
                placeholder="Enter a keyword or tag..."
                error={!!keywordsError}
                helperText={
                  keywordsError ||
                  'Press Enter or click Add to include the keyword'
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                onClick={addKeyword}
                startIcon={<AddIcon />}
                disabled={!newKeyword.trim()}
                sx={{ height: '56px' }}
                fullWidth
              >
                Add Keyword
              </Button>
            </Grid>
          </Grid>

          {/* Selected Keywords */}
          {(formData.visibilityMatching.keywords || []).length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Keywords ({(formData.visibilityMatching.keywords || []).length}
                ):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(formData.visibilityMatching.keywords || []).map(keyword => (
                  <Chip
                    key={keyword}
                    label={keyword}
                    onDelete={() => removeKeyword(keyword)}
                    color="info"
                    variant="outlined"
                    deleteIcon={<CloseIcon />}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Keyword Tips:</strong> Include conditions (e.g., "back
              pain", "stroke recovery"), treatment types (e.g., "manual
              therapy", "exercise"), and patient demographics (e.g.,
              "pediatric", "geriatric") to improve matching.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Search Visibility */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Search Visibility
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Control whether your service package appears in patient search
            results.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={formData.visibilityMatching.showInSearch}
                onChange={e =>
                  onUpdate({
                    visibilityMatching: {
                      ...formData.visibilityMatching,
                      showInSearch: e.target.checked,
                    },
                  })
                }
                color="primary"
              />
            }
            label="Show in Search Results"
            sx={{ mb: 2 }}
          />

          {formData.visibilityMatching.showInSearch && (
            <Alert severity="success">
              <Typography variant="body2">
                <strong>Visible in Search:</strong> Your service package will
                appear in patient search results when they match your
                specialties, body regions, and keywords.
              </Typography>
            </Alert>
          )}

          {!formData.visibilityMatching.showInSearch && (
            <Alert severity="warning">
              <Typography variant="body2">
                <strong>Hidden from Search:</strong> Your service package will
                not appear in search results. Patients can only access it
                through direct links or referrals.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {isEditMode
              ? 'Finalize Your Updates'
              : 'Finalize Your Service Package'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {isEditMode
              ? isPublishedEdit
                ? 'Choose to stash your changes as a draft or push them live by publishing right away.'
                : 'Keep refining this draft or publish it to make it available to patients.'
              : 'You can save your service package as a draft to continue editing later, or publish it to make it available to patients.'}
          </Typography>

          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={onSaveAsDraft}
                disabled={isSubmitting}
                sx={{ minWidth: 200 }}
              >
                {isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  draftButtonLabel
                )}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={onPublish}
                disabled={isSubmitting}
                sx={{ minWidth: 200 }}
              >
                {isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  publishButtonLabel
                )}
              </Button>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              {isEditMode ? (
                isPublishedEdit ? (
                  <>
                    <strong>Tip:</strong> Saving as a draft lets you review
                    everything before republishing. When you choose publish, the
                    updated package is immediately visible based on your
                    visibility settings.
                  </>
                ) : (
                  <>
                    <strong>Tip:</strong> Keep the package in draft while you
                    fine-tune it, or publish to make the latest updates visible
                    to patients.
                  </>
                )
              ) : (
                <>
                  <strong>Publishing:</strong> Once published, your service
                  package will be visible to patients based on your search
                  visibility settings. You can still edit it after publishing.
                </>
              )}
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  )
}
